import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useDispatch } from 'react-redux';
import { useAuth } from '@lb-classroom/feature-auth';
import { setTeamsContactEmail as setTeamsContactEmailAction } from '@actions/currentUser';
import UserValidator from '@root/validators/UserValidator';
import type { SetTeamsModalContainerProps } from './set-teams-modal.types';
import type { AppDispatch } from '@root/store';

const TEAMS_ACCOUNT_URLS: Record<string, string> = {
  en: 'https://www.microsoft.com/en-us/microsoft-teams/free',
  ja: 'https://www.microsoft.com/ja-jp/microsoft-teams/free',
  es: 'https://www.microsoft.com/es-es/microsoft-teams/free',
  pt: 'https://www.microsoft.com/pt-br/microsoft-teams/free',
  tw: 'https://www.microsoft.com/zh-tw/microsoft-teams/free',
  ru: 'https://www.microsoft.com/ru-ru/microsoft-teams/free',
};

const getTeamsAccountUrl = (locale: string): string => {
  const baseLocale = locale.split('-')[0].toLowerCase();
  return TEAMS_ACCOUNT_URLS[baseLocale] || TEAMS_ACCOUNT_URLS.en;
};

export const useSetTeamsModalService = (props: SetTeamsModalContainerProps) => {
  const {
    hideModal,
    addModalMessage,
    clearModalMessage,
    justSignedUp = false,
  } = props;

  const { t, i18n } = useTranslation('common');
  const { currentUser } = useAuth();
  const dispatch = useDispatch<AppDispatch>();

  const [teamsEmail, setTeamsEmail] = useState(
    currentUser?.teams_contact_email || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTeamsEmailChange = useCallback((value: string) => {
    setTeamsEmail(value);
  }, []);

  const handleUseAccountEmail = useCallback(() => {
    if (currentUser?.email) {
      setTeamsEmail(currentUser.email);
    }
  }, [currentUser?.email]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      const validation = await UserValidator.teamsContactEmailValidator({
        name: t('form.teamsContactEmail'),
        value: teamsEmail,
        required: true,
        t,
      });

      if (!validation.success) {
        addModalMessage(validation.message, {
          type: 'error',
          autoDisappear: false,
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        // Dispatch the Redux action which handles API call, state update, modal hiding, and flash message
        await dispatch(setTeamsContactEmailAction(teamsEmail));
      } catch (error) {
        console.error('Failed to set Teams contact email:', error);
        addModalMessage(t('form.submitError', 'An error occurred. Please try again.'), {
          type: 'error',
          autoDisappear: false,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [teamsEmail, t, addModalMessage, clearModalMessage, dispatch],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const teamsAccountUrl = getTeamsAccountUrl(i18n.language || 'en');

  return {
    teamsEmail,
    accountEmail: currentUser?.email,
    isSubmitting,
    justSignedUp,
    teamsAccountUrl,
    message: props.message,
    messageType: props.messageType,
    onTeamsEmailChange: handleTeamsEmailChange,
    onUseAccountEmail: handleUseAccountEmail,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
